// ============================================================
// liveEvents.ts — (D12 live updates) the FIRM-level and USER-level halves of
// the change bus (the case half is utils/caseChangeEvents).
//
// hooks/useLiveTopics turns the server's `firm-changed` / `user-changed`
// messages (and every catch-up: reconnect, `live-resync`, the tab coming
// back, a moved stamp) into these; firm lists / dashboards / the bell listen
// and refetch through their normal APIs.
//   useFirmChange(['cases', 'tasks'], () => reload())
//   useUserChange(['notifications'], () => loadBell())
// ============================================================
import { useEffect, useRef } from 'react'

export type FirmTopic = 'cases' | 'clients' | 'tasks' | 'calendar' | 'billing' | 'documents' | 'settings' | 'users' | 'deadlineTypes'
export const ALL_FIRM_TOPICS: readonly FirmTopic[] = ['cases', 'clients', 'tasks', 'calendar', 'billing', 'documents', 'settings', 'users', 'deadlineTypes']
// notifications = the bell; favourites / preferences (colour scheme, Microsoft
// connection) = changed in another of this user's tabs.
export type UserTopic = 'notifications' | 'favourites' | 'preferences'
export const ALL_USER_TOPICS: readonly UserTopic[] = ['notifications', 'favourites', 'preferences']

export interface LiveChangeInfo {
  // The topics of this change that the listener watches.
  topics: string[]
  actorUserId?: string | null
  // A catch-up re-read, not a specific change.
  resync?: boolean
}

const FIRM_EVENT = 'casevo:firm-change'
const USER_EVENT = 'casevo:user-change'
interface Detail { topics: string[]; actorUserId?: string | null; resync?: boolean }

const emit = (name: string, d: Detail): void => {
  if (typeof window === 'undefined' || !d.topics.length) return
  window.dispatchEvent(new CustomEvent<Detail>(name, { detail: d }))
}
export const emitFirmChange = (topics: readonly string[], meta: { actorUserId?: string | null; resync?: boolean } = {}): void =>
  emit(FIRM_EVENT, { topics: [...topics], ...meta })
export const emitUserChange = (topics: readonly string[], meta: { resync?: boolean } = {}): void =>
  emit(USER_EVENT, { topics: [...topics], ...meta })

const useBus = (name: string, topics: readonly string[] | '*', fn: (info: LiveChangeInfo) => void, enabled: boolean): void => {
  const fnRef = useRef(fn)
  fnRef.current = fn
  const key = topics === '*' ? '*' : [...topics].sort().join(',')
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return undefined
    const want = key === '*' ? null : new Set(key.split(','))
    const on = (e: Event): void => {
      const d = (e as CustomEvent<Detail>).detail
      if (!d) return
      const matched = want ? d.topics.filter((t) => want.has(t)) : d.topics
      if (!matched.length) return
      fnRef.current({ topics: matched, actorUserId: d.actorUserId, resync: d.resync })
    }
    window.addEventListener(name, on)
    return () => window.removeEventListener(name, on)
  }, [name, key, enabled])
}

export const useFirmChange = (topics: readonly FirmTopic[] | '*', fn: (info: LiveChangeInfo) => void, enabled = true): void =>
  useBus(FIRM_EVENT, topics, fn, enabled)
export const useUserChange = (topics: readonly UserTopic[] | '*', fn: (info: LiveChangeInfo) => void, enabled = true): void =>
  useBus(USER_EVENT, topics, fn, enabled)
